import { ActivityIndicator } from '@capsicum/common-web';
import { NCOnboardingAnalyticsEvents, useNCWOnboardingRoutes } from '@capsicum/features/nc-onboarding/shared';
import { useNonCustodialProviderUserWalletStatus } from '@socios-blk/core';
import { useEffect } from 'react';
import styled from 'styled-components';

import { useNCRegistrationWeb } from '../../hooks';

import { NcRegistrationRecoveryKit } from './NcRegistrationRecoveryKit';
import { NCRegistrationStart } from './NcRegistrationStart';

type NCRegistrationProps = {
	onSuccess: () => void;
	variant?: 'INIT_FLOW' | 'PAGE';
	waitForWalletStatus?: boolean;
};

export const NCRegistration = ({ onSuccess, variant = 'PAGE', waitForWalletStatus = true }: NCRegistrationProps) => {
	const { isError, isLoading, isNCWalletRegistered, isSmartWalletRegistered } =
		useNonCustodialProviderUserWalletStatus();
	const {
		isRegistrationStarted,
		openNCRegistrationConfirmDrawer,
		openNCRegistrationExistingUserDrawer,
		recoveryKit,
		startRegistration
	} = useNCRegistrationWeb(onSuccess);
	const { triggerAnalyticsEvent } = useNCWOnboardingRoutes({});

	const Container = variant === 'PAGE' ? S.PageContainer : S.InitFlowContainer;

	const isUserRegistered = !isError && !isLoading && isNCWalletRegistered && isSmartWalletRegistered;

	useEffect(() => {
		triggerAnalyticsEvent(NCOnboardingAnalyticsEvents.LAND_ON_REGISTER_PAGE);
	}, [triggerAnalyticsEvent]);

	// Open a drawer indicating the wallet is already created
	useEffect(() => {
		if (!isRegistrationStarted && !recoveryKit && isUserRegistered) {
			openNCRegistrationExistingUserDrawer();
		}
	}, [isRegistrationStarted, isUserRegistered, recoveryKit]);

	if (isLoading && waitForWalletStatus) {
		return (
			<Container>
				<ActivityIndicator />
			</Container>
		);
	}

	/**
	 * If recovery kit is available, display the recovery kit screen.
	 */
	return (
		<Container>
			{recoveryKit ? (
				<NcRegistrationRecoveryKit
					code={recoveryKit.code}
					keyId={recoveryKit.keyId}
					onAccept={openNCRegistrationConfirmDrawer}
				/>
			) : (
				<NCRegistrationStart
					isContinueButtonDisabled={!!isUserRegistered}
					isContinueButtonLoading={isRegistrationStarted}
					onClickContinue={startRegistration}
				/>
			)}
		</Container>
	);
};

namespace S {
	export const Container = styled.div`
		display: flex;
		flex-flow: column;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxlarge};
		padding: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const PageContainer = styled(Container)`
		width: 100%;
		max-width: 500px;
		margin: ${({ theme }) => theme.spacings.xlarge} auto 0;
		margin-top: ${({ theme }) => theme.spacings.xlarge};
	`;

	export const InitFlowContainer = styled(Container)`
		height: 100%;
		margin: 0 auto;
		max-width: 500px;
	`;
}
